/**
 * PromptCard - Reusable prompt display component
 * Shows prompt preview with image, metadata, and interaction buttons
 * Supports lazy loading, WebP format, and responsive design
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Button from '../ui/Button';
import ReportModal from './ReportModal';
import { notificationService } from '../../services/notificationService';
import { getPreviewUrl } from '../../utils/imagePathHelper';

interface PromptCardProps {
  prompt: {
    public_id: string;
    slug?: string | null;   // SEO-friendly slug — use for URL when available
    text: string;
    preview_url?: string;
    preview_image?: string;
    view_count: number;
    created_at: string;
    user?: {
      public_id: string;
      name: string;
      verified: boolean;
    };
    categories: Array<{
      id: number;
      type: string;
      name: string;
    }>;
    tags?: Array<{
      id: number;
      name: string;
      slug: string;
    }>;
  };
  // onEdit?: (promptId: string) => void;
  onDelete?: (promptId: string) => void;
  showActions?: boolean;
  className?: string;
  showCounts?: boolean;
}

export default function PromptCard({
  prompt,
  // onEdit,
  onDelete,
  showActions = false,
  className = '',
  showCounts = true,
}: PromptCardProps) {
  const { state } = useAuth();
  const { isAuthenticated } = state;
  const navigate = useNavigate();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  // Use thumbnail for card display (optimized for grid/list views)
  // Database stores only filename, we construct the thumbnail URL
  const preview_img_route = getPreviewUrl(prompt.preview_image);
  const authorName = prompt.user?.name?.trim() || 'Unknown Creator';
  const authorInitial = authorName.charAt(0).toUpperCase();
  const isAuthorVerified = prompt.user?.verified ?? false;


  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const truncateText = (text: string, maxLength: number = 150) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getCategoryLabel = (type: string) => {
    const category = prompt.categories?.find((item) => item.type.toLowerCase() === type.toLowerCase());
    return category?.name;
  };

  const primaryCategories = ['ai model', 'model', 'style', 'platform'];

  // Build canonical URL: prefer slug, fall back to public_id under the unified /p/ route
  const promptPath = prompt.slug ? `/p/${prompt.slug}` : `/p/${prompt.public_id}`;

  const handleCardClick = () => {
    navigate(promptPath);
  };

  const handleActionClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
  };

  const handleReportClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowReportModal(true);
  };

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `${window.location.origin}${promptPath}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Check out this AI prompt',
          url: url,
        });
      } catch (error) {
        // User cancelled sharing
      }
    } else {
      // Fallback to clipboard
      try {
        await navigator.clipboard.writeText(url);
        notificationService.success('Link copied to clipboard!');
      } catch (error) {
        notificationService.error('Failed to copy link');
      }
    }
  };

  return (
    <>
      <div
        onClick={handleCardClick}
        className={`bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden cursor-pointer ${className}`}
      >
        {/* Image */}
        <div className="relative bg-gray-100 min-h-[14rem] md:min-h-[16rem] flex flex-wrap content-around">
          {preview_img_route ? (
            <>
              {!imageLoaded && !imageError && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              )}
              <img
                src={preview_img_route}
                alt="Prompt preview"
                className={`w-full h-full object-cover transition-opacity duration-200 ${imageLoaded ? 'opacity-100' : 'opacity-0'
                  }`}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
                loading="lazy"
              />
              {imageError && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                  <svg
                    className="w-12 h-12 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              )}
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100">
              <svg
                className="w-16 h-16 text-primary-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
          )}

          {/* Quick Actions Overlay */}
          <div className="absolute top-2 right-2 flex space-x-1">
            <button
              onClick={handleShare}
              className="p-1.5 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-colors"
              title="Share prompt"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
              </svg>
            </button>
            <button
              onClick={handleReportClick}
              className="p-1.5 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-colors"
              title="Report prompt"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Prompt Text */}
          <p className="text-gray-900 font-medium mb-3 hover:text-primary-600 transition-colors">
            {truncateText(prompt.text)}
          </p>

          {/* Categories & Tags */}
          <div className="flex flex-wrap gap-2 mb-4">
            {prompt.categories?.length ? (
              <>
                {primaryCategories
                  .map((type) => ({ type, label: getCategoryLabel(type) }))
                  .filter((item) => item.label)
                  .map((item, index) => {
                    const colorClasses = [
                      'bg-blue-100 text-blue-800',
                      'bg-green-100 text-green-800',
                      'bg-purple-100 text-purple-800',
                      'bg-indigo-100 text-indigo-800',
                    ];
                    const color = colorClasses[index % colorClasses.length];
                    return (
                      <span
                        key={item.type}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}
                      >
                        {item.label}
                      </span>
                    );
                  })}
                {prompt.categories
                  .filter((cat) => !primaryCategories.includes(cat.type.toLowerCase()))
                  .map((cat) => (
                    <span
                      key={cat.id}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                    >
                      {cat.name}
                    </span>
                  ))}
              </>
            ) : (
              <span className="text-xs text-gray-500">No categories provided.</span>
            )}

            {prompt.tags?.length
              ? prompt.tags.map((tag) => (
                <span
                  key={tag.id}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                >
                  #{tag.name}
                </span>
              ))
              : null}
          </div>

          {/* Author and Date */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-medium">
                  {authorInitial}
                </span>
              </div>
              <div className="text-sm">
                <span className="text-gray-900 font-medium">
                  {authorName}
                  {isAuthorVerified && (
                    <svg className="inline w-4 h-4 ml-1 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                </span>
                <p className="text-gray-500 text-xs">
                  {formatDate(prompt.created_at)}
                </p>
              </div>
            </div>
            {showCounts && (
              <div className="text-xs text-gray-500">
                {prompt.view_count} views
              </div>
            )}
          </div>


          {/* Owner Actions */}
          {showActions && isAuthenticated && prompt.user?.public_id === state.user?.public_id && (
            <div className="flex justify-end space-x-2 mt-3 pt-3 border-t border-gray-100">
              <Button
                onClick={(e) => handleActionClick(e as any, () => onDelete?.(prompt.public_id))}
                variant="danger"
                size="sm"
              >
                Delete
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Report Modal */}
      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        promptId={prompt.public_id}
      />
    </>
  );
}